import { supabase } from '../lib/supabase';

interface LibraryComponent {
  user_id: string;
  component_name: string;
  symbol?: string;
  color: string;
  reference_image?: string;
  material_cost?: number;
  labor_unit_type?: 'E' | 'C' | 'M';
  labor_units?: {
    normal: number;
    difficult: number;
    very_difficult: number;
  };
  preferred_labor_condition?: 'normal' | 'difficult' | 'very_difficult';
  custom_labor_hours?: number;  // Custom override value
}

export const componentLibraryService = {
  /**
   * Get user's entire component library
   */
  async getUserLibrary(userId: string) {
    try {
      const { data, error } = await supabase
        .from('user_component_library')
        .select('*')
        .eq('user_id', userId)
        .order('component_name');
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to load component library:', error);
      return [];
    }
  },

  /**
   * Sync a single component to library (creates or updates)
   * Checks for existing component case-insensitively to avoid duplicates
   */
  async syncComponentToLibrary(userId: string, component: Partial<LibraryComponent>) {
    try {
      // First check if component exists with different case
      const { data: existing } = await supabase
        .from('user_component_library')
        .select('*')
        .eq('user_id', userId)
        .ilike('component_name', component.component_name || '');
      
      let componentNameToUse = component.component_name;
      
      // If exists with different case, use the existing name to update it
      if (existing && existing.length > 0) {
        componentNameToUse = existing[0].component_name;
        console.log(`📝 Component exists as "${componentNameToUse}", will update it`);
      }
      
      const { data, error } = await supabase
        .from('user_component_library')
        .upsert({
          user_id: userId,
          component_name: componentNameToUse,
          symbol: component.symbol,
          color: component.color,
          reference_image: component.reference_image,
          material_cost: component.material_cost,
          labor_unit_type: component.labor_unit_type,
          labor_units: component.labor_units,
          preferred_labor_condition: component.preferred_labor_condition,
          custom_labor_hours: component.custom_labor_hours,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,component_name'
        })
        .select()
        .single();
      
      if (error) throw error;
      
      console.log(`✅ Synced "${componentNameToUse}" to library`);
      return data;
    } catch (error) {
      console.error('Failed to sync to library:', error);
      // Graceful degradation - don't throw
      return null;
    }
  },

  /**
   * Increment usage counter for a component
   */
  async incrementUsage(userId: string, componentName: string) {
    try {
      const { error } = await supabase.rpc('increment_component_usage', {
        p_user_id: userId,
        p_component_name: componentName
      });
      
      if (error) throw error;
    } catch (error) {
      console.error('Failed to increment usage:', error);
    }
  },

  /**
   * Sync only reference image (for autocount setup)
   */
  async syncReferenceImage(userId: string, componentName: string, referenceImage: string) {
    try {
      const { error } = await supabase
        .from('user_component_library')
        .update({ 
          reference_image: referenceImage,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('component_name', componentName);
      
      if (error) throw error;
      console.log(`✅ Synced reference image for "${componentName}"`);
    } catch (error) {
      console.error('Failed to sync reference image:', error);
    }
  },

  /**
   * Sync pricing data from estimate table
   */
  async syncPricingData(userId: string, componentName: string, pricingData: {
    material_cost?: number;
    labor_unit_type?: 'E' | 'C' | 'M';
    labor_units?: any;
    preferred_labor_condition?: string;
    custom_labor_hours?: number;  // Custom override value
  }) {
    try {
      // First check if component exists
      const { data: existing } = await supabase
        .from('user_component_library')
        .select('component_name, color, symbol')
        .eq('user_id', userId)
        .ilike('component_name', componentName)
        .maybeSingle();
      
      const upsertData: any = {
        user_id: userId,
        component_name: existing?.component_name || componentName,
        material_cost: pricingData.material_cost,
        labor_unit_type: pricingData.labor_unit_type,
        labor_units: pricingData.labor_units,
        preferred_labor_condition: pricingData.preferred_labor_condition,
        updated_at: new Date().toISOString()
      };
      
      // Only include custom_labor_hours if it's defined (allows clearing it)
      if (pricingData.custom_labor_hours !== undefined) {
        upsertData.custom_labor_hours = pricingData.custom_labor_hours;
      }
      
      // If component doesn't exist, add required fields with defaults
      if (!existing) {
        upsertData.color = '#808080';  // Default gray
        upsertData.symbol = componentName;
      } else {
        // Preserve existing color and symbol
        upsertData.color = existing.color;
        upsertData.symbol = existing.symbol || componentName;
      }
      
      const { error } = await supabase
        .from('user_component_library')
        .upsert(upsertData, {
          onConflict: 'user_id,component_name'
        });
      
      if (error) throw error;
      console.log(`✅ Synced pricing for "${componentName}"${pricingData.custom_labor_hours ? ' (with custom hours)' : ''}`);
    } catch (error) {
      console.error('Failed to sync pricing:', error);
    }
  },

  /**
   * Automatically seed a new project from user's library
   */
  async seedProjectFromLibrary(estimationId: string, userId: string) {
    try {
      // Get user's library
      const library = await this.getUserLibrary(userId);
      
      if (!library || library.length === 0) {
        console.log('📝 No library components - starting fresh project');
        return null;
      }

      console.log(`🌱 Auto-seeding project with ${library.length} components from library`);

      // Convert library to project_key format
      const projectKey = {
        components: library.map(lib => ({
          name: lib.component_name,
          symbol: lib.symbol || lib.component_name,
          color: lib.color,
          referenceImage: lib.reference_image,
          pages: [],
          totalCount: 0
        })),
        extractedAt: new Date().toISOString()
      };

      // Build cost_info with pricing data from library
      const costInfo = {
        laborRate: 75,
        overhead: 15,
        profit: 10,
        components: library.reduce((acc, lib) => {
          acc[lib.component_name] = {
            laborCondition: lib.preferred_labor_condition || 'normal',
            materialCost: lib.material_cost || 0,
            laborUnitType: lib.labor_unit_type || 'E',
            laborUnits: lib.labor_units || { 
              normal: 0, 
              difficult: 0, 
              very_difficult: 0 
            },
            quantity: 0  // Will be populated as user counts
          };
          return acc;
        }, {} as any),
        lastUpdated: new Date().toISOString()
      };

      // Save both to estimation
      const { error } = await supabase
        .from('estimations')
        .update({ 
          project_key: projectKey,
          cost_info: costInfo
        })
        .eq('id', estimationId);

      if (error) throw error;

      console.log(`✅ Project auto-seeded with ${library.length} components`);
      return projectKey;
    } catch (error) {
      console.error('Failed to seed project from library:', error);
      // Don't throw - let project creation succeed even if seeding fails
      return null;
    }
  }
};
